/**
 * La red de seguridad del chat: el tiempo real es una OPTIMIZACIÓN, no la fuente de verdad.
 *
 * ⭐ QUÉ HACE Y QUÉ NO HACE — la distinción importa, porque confundirlas ya costó caro.
 *
 * ESTO NO ARREGLA EL CANAL NI LO TOCA. El canal en vivo lo mantiene supabase-js, que ya trae su
 * propia reconexión. Este archivo hace otra cosa: cada tanto le pregunta al servidor —la única
 * fuente de verdad— si hay algo posterior a lo que está en pantalla. Si el canal anda, esto no
 * encuentra nada y cuesta una consulta liviana por índice. Si el canal se pierde un evento, esto lo
 * rescata sin que nadie tenga que refrescar.
 *
 * 🚨 NO ES EL MOTOR DEL CHAT Y NO DEBE SERLO. Si los mensajes están llegando por acá, algo está mal
 * en el canal: se nota porque tardan hasta un minuto en aparecer. Eso es una señal de falla, no el
 * funcionamiento normal.
 *
 * 🪤 SE PREGUNTA POR LO NUEVO, NO SE RECARGA TODO. «Traeme los mensajes posteriores al último que
 * tengo» es una consulta por índice que casi siempre vuelve vacía. Recargar el hilo entero cada
 * tanto también funcionaría, pero paga cincuenta filas y una escritura de «marcar leído» cada vez,
 * por algo que el 99% de las veces no cambió nada.
 */
import { supabase } from '@/lib/supabase';
import { aplicarAlHilo, conversacionesConHiloAbierto } from '@/hooks/waThreadRegistry';
import type { WaMessage } from '@/components/whatsapp/WaMessageThread';
import { isRenderableMessage } from '@/lib/wa-utils';
import { playReceiveSound } from '@/lib/whatsappSounds';
import { medirLlegadaDeMensaje, type IdentidadRum } from '@/lib/whatsapp/medicionDelChat';

/**
 * Cada cuánto se pasa a revisar. Es una red, no un motor: va lento a propósito.
 *
 * 🪤 SUBIR ESTE NÚMERO NO ES LA FORMA DE HACER EL CHAT MÁS RÁPIDO. Si hace falta bajarlo para que
 * los mensajes se sientan inmediatos, el problema está en el canal y hay que arreglarlo ahí.
 */
export const CADENCIA_MS = 60_000;

/** Cuántos mensajes se traen como máximo por vuelta. Si hay más, la próxima sigue. */
const TOPE = 50;

/**
 * Trae de la base los mensajes de esta conversación posteriores al último que hay en pantalla.
 * Devuelve cuántos faltaban — cero es el caso normal y esperado.
 */
export async function reconciliarConversacion(
  conversationId: string,
  tenantId: string,
  identidad?: IdentidadRum,
): Promise<number> {
  // Se lee el último conocido a través del propio registro: el hilo es el que sabe qué tiene.
  let desde: string | null = null;
  aplicarAlHilo(conversationId, (prev) => {
    const ultimo = prev[prev.length - 1];
    desde = ultimo?.created_at ?? null;
    return prev; // sólo se está leyendo
  });

  let consulta = supabase
    .from('wa_messages')
    .select('*')
    .eq('tenant_id', tenantId)
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true })
    .limit(TOPE);
  if (desde) consulta = consulta.gt('created_at', desde);

  const { data, error } = await consulta;
  if (error || !data?.length) return 0;

  let agregados = 0;
  aplicarAlHilo(conversationId, (prev) => {
    const conocidos = new Set(prev.map((m) => m.id));
    /*
     * 🪤 SE DESCARTAN LOS QUE YA ESTÁN Y LOS QUE NO SE DIBUJAN. El push pudo haber traído algunos
     * mientras la consulta viajaba —duplicarlos sería peor que perderlos— y las reacciones viajan
     * como filas de mensaje sin ser mensajes.
     */
    const faltantes = (data as unknown as WaMessage[])
      .filter((m) => !conocidos.has(m.id) && isRenderableMessage(m));
    agregados = faltantes.length;
    /*
     * 🚨 CADA MENSAJE RESCATADO ACÁ ES UN FALLO DEL TIEMPO REAL, y se registra como tal.
     *
     * Este sondeo existe como red de seguridad: si trae algo, es porque el canal en vivo se lo
     * perdió. Sin medirlo, esa pérdida es invisible —el mensaje aparece, tarde, y nadie sabe que
     * llegó por la puerta de atrás—. Con la medición, `/chat/reconciliacion` en el tablero cuenta
     * exactamente cuántas veces por día el chat no se enteró.
     */
    if (identidad) {
      for (const m of faltantes) medirLlegadaDeMensaje('reconciliacion', m.created_at, identidad);
    }
    /*
     * 🚨 Y SUENA, COMO SI HUBIERA LLEGADO POR EL CANAL. El founder lo notó por separado: «tampoco
     * suena el mensaje cuando llega, como antes pasaba». Es el mismo problema visto de otro lado —
     * el sonido vivía sólo en el manejador del canal, así que un mensaje rescatado por esta vía
     * entraba mudo. Para el operador son la misma cosa: llegó un mensaje del cliente.
     *
     * 🪤 Una sola vez por vuelta, no una por mensaje: cinco mensajes rescatados juntos son una
     * novedad, no cinco alarmas.
     */
    if (faltantes.some((m) => m.direction === 'inbound')) playReceiveSound();
    return faltantes.length ? [...prev, ...faltantes] : prev;
  });
  return agregados;
}

/** Reconcilia todas las conversaciones que estén abiertas en pantalla. */
export async function reconciliarLoAbierto(tenantId: string, identidad?: IdentidadRum): Promise<number> {
  const abiertas = conversacionesConHiloAbierto();
  if (!abiertas.length) return 0;
  const resultados = await Promise.all(abiertas.map((id) => reconciliarConversacion(id, tenantId, identidad).catch(() => 0)));
  return resultados.reduce((a, b) => a + b, 0);
}
